document.addEventListener('DOMContentLoaded', () => {
  // Every carousel section includes this file, so it can load more than
  // once per page - bind everything only on the first run, otherwise each
  // arrow click fires once per copy of the script.
  if (window.bsCarouselInitialized) return;
  window.bsCarouselInitialized = true;

  document.querySelectorAll('[data-best-sellers-tabs]').forEach((tabs) => {
    const buttons = Array.from(tabs.querySelectorAll('[data-best-sellers-tab]'));
    const section = tabs.closest('.best-sellers');
    if (!section) return;

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.classList.contains('is-active')) return;

        buttons.forEach((btn) => {
          btn.classList.remove('is-active');
          btn.setAttribute('aria-selected', 'false');
        });
        button.classList.add('is-active');
        button.setAttribute('aria-selected', 'true');

        const targetId = button.dataset.target;
        section.querySelectorAll('[data-best-sellers-panel]').forEach((panel) => {
          panel.hidden = panel.id !== targetId;
          panel.classList.toggle('is-active', panel.id === targetId);
        });

        // The newly revealed panel's track was hidden (0 width) when first
        // measured on load, so recompute its arrow visibility now.
        window.dispatchEvent(new Event('resize'));
      });
    });
  });

  document.querySelectorAll('[data-bs-track]').forEach((track) => {
    // The arrows are siblings of the track in every carousel that uses
    // this script (Best-sellers, Avis clients), whatever the wrapper class.
    const wrapper = track.parentElement;
    if (!wrapper) return;
    const prevButton = wrapper.querySelector('[data-bs-prev]');
    const nextButton = wrapper.querySelector('[data-bs-next]');
    if (!prevButton || !nextButton) return;

    // Browsers can restore a scrolled position for this track on reload
    // (scroll anchoring / bfcache), which breaks the left-aligned-with-title
    // starting layout. Force it back to the start on every load.
    track.scrollLeft = 0;

    const getStep = () => {
      const item = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return item ? item.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
    };

    if (track.hasAttribute('data-bs-loop') && setUpLoop(track, prevButton, nextButton, getStep)) return;

    const updateVisibility = () => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      nextButton.hidden = maxScroll <= 4;
      prevButton.hidden = track.scrollLeft <= 4;
      if (maxScroll <= 4 || track.scrollLeft >= maxScroll - 4) {
        nextButton.hidden = true;
      }
    };

    nextButton.addEventListener('click', () => {
      track.scrollBy({ left: getStep(), behavior: 'smooth' });
    });

    prevButton.addEventListener('click', () => {
      track.scrollBy({ left: -getStep(), behavior: 'smooth' });
    });

    track.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', updateVisibility);
    updateVisibility();
  });
});

// Endless carousel (data-bs-loop): copies of the items are added before
// and after the originals, and the track is kept scrolled inside the
// originals. Whenever scrolling settles in a copy, it jumps by whole set
// widths - landing on an identical-looking card, so the wrap is invisible
// and the arrows never run out, in either direction.
// Returns false (leaving the regular behaviour) when the items all fit.
function setUpLoop(track, prevButton, nextButton, getStep) {
  if (track.scrollWidth - track.clientWidth <= 4) return false;

  const originals = Array.from(track.children);
  // On phones a finger flick can travel many cards before it settles, so
  // one copy per side ran out and the row stopped dead at its end. Give
  // phones more copies of runway; desktop (arrows, one card per click)
  // keeps a single copy per side.
  const copies = window.matchMedia('(max-width: 749px)').matches ? 4 : 1;
  const makeClone = (item) => {
    const clone = item.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('data-shopify-editor-block');
    clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
    return clone;
  };
  for (let i = 0; i < copies; i++) {
    originals.forEach((item) => track.appendChild(makeClone(item)));
    originals
      .slice()
      .reverse()
      .forEach((item) => track.insertBefore(makeClone(item), track.firstChild));
  }
  const firstIndex = originals.length * copies;
  // Lets the CSS drop the track's side padding: the clones now fill both
  // sides, so the row can run edge to edge.
  track.classList.add('is-looping');

  // Distance between an original and its copy = width of one full set.
  const getSetWidth = () =>
    track.children[firstIndex + originals.length].offsetLeft - track.children[firstIndex].offsetLeft;

  // Where the first card should rest: the track's scroll-padding-left
  // (e.g. the page margin). It can be a calc() with %, so resolve it by
  // sizing a throwaway element with it inside the track's container.
  const getInset = () => {
    const value = getComputedStyle(track).scrollPaddingLeft;
    if (!value || value === 'auto') return 0;
    const probe = document.createElement('div');
    // display:block overrides the theme's `div:empty { display: none }`.
    probe.style.cssText = `display:block;position:absolute;visibility:hidden;height:0;width:${value}`;
    track.parentElement.appendChild(probe);
    const inset = probe.offsetWidth;
    probe.remove();
    return inset;
  };

  const jumpTo = (left) => {
    const behavior = track.style.scrollBehavior;
    track.style.scrollBehavior = 'auto';
    track.scrollLeft = left;
    track.style.scrollBehavior = behavior;
  };

  // Keep the resting card within the original set:
  // [copies * setWidth - inset, (copies + 1) * setWidth - inset).
  let touching = false;
  const recenter = () => {
    // Never move the row under a finger that's still on it.
    if (touching) return;
    const setWidth = getSetWidth();
    const start = copies * setWidth - getInset();
    const offset = track.scrollLeft - start;
    if (offset < -4 || offset >= setWidth - 4) {
      jumpTo(track.scrollLeft - Math.floor((offset + 4) / setWidth) * setWidth);
    }
  };

  // Start on the first original card, resting on the page margin, with
  // the clones before it filling the space to the left edge.
  // (The clones before the originals are exactly `copies` set widths long.)
  jumpTo(copies * getSetWidth() - getInset());
  prevButton.hidden = false;
  nextButton.hidden = false;

  nextButton.addEventListener('click', () => {
    track.scrollBy({ left: getStep(), behavior: 'smooth' });
  });
  prevButton.addEventListener('click', () => {
    track.scrollBy({ left: -getStep(), behavior: 'smooth' });
  });

  // Wait for smooth/momentum scrolling to settle before wrapping, so the
  // jump never happens mid-animation.
  let settleTimer;
  track.addEventListener(
    'scroll',
    () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(recenter, 120);
    },
    { passive: true }
  );
  track.addEventListener('touchstart', () => (touching = true), { passive: true });
  const release = () => {
    touching = false;
    clearTimeout(settleTimer);
    settleTimer = setTimeout(recenter, 120);
  };
  track.addEventListener('touchend', release, { passive: true });
  track.addEventListener('touchcancel', release, { passive: true });
  window.addEventListener('resize', recenter);

  return true;
}
